// Guide illustrations in the brand's only illustration language (BRAND.md, Imagery): flat blocks of the
// zoning colours, 1px ink edges, hatching and legends. No images: inline SVG keeps the prerender readable
// and costs no requests. Every colour is named on the drawing or in its legend (The Legend Rule).
import { ReactNode } from 'react';

export type GuideIllustrationId =
  | 'costStack' | 'commute' | 'floorPlan' | 'energy' | 'lightAndNoise' | 'documents' | 'tradeOff' | 'match'
  | 'imtJovem' | 'stampDuty' | 'deedCosts' | 'bankCosts' | 'building' | 'checklistSheet';

type Fill = 'hab' | 'equip' | 'tint' | 'verde' | 'paper' | 'plain' | 'hatch';

function Block({ x, y, w, h, fill = 'plain' }: { x: number; y: number; w: number; h: number; fill?: Fill }) {
  return (
    <>
      <rect className={`gi-block gi-${fill === 'hatch' ? 'paper' : fill}`} x={x} y={y} width={w} height={h} />
      {fill === 'hatch' && <rect className="gi-hatch" x={x} y={y} width={w} height={h} />}
    </>
  );
}

function Label({ x, y, children, anchor = 'start', onFill = false, mono = false, strong = false }: {
  x: number; y: number; children: ReactNode; anchor?: 'start' | 'middle' | 'end'; onFill?: boolean; mono?: boolean; strong?: boolean;
}) {
  const classes = ['gi-text', onFill && 'gi-text-on-fill', mono && 'gi-text-mono', strong && 'gi-text-strong'].filter(Boolean).join(' ');
  return <text className={classes} x={x} y={y} textAnchor={anchor}>{children}</text>;
}

function Tag({ x, y, w, children }: { x: number; y: number; w: number; children: ReactNode }) {
  return (
    <g className="gi-tag">
      <rect x={x} y={y} width={w} height={26} />
      <text x={x + w / 2} y={y + 18} textAnchor="middle">{children}</text>
    </g>
  );
}

function Pin({ x, y, w = 72, children }: { x: number; y: number; w?: number; children: ReactNode }) {
  return (
    <g className="gi-pin">
      <rect x={x} y={y} width={w} height={28} />
      <path d={`M${x + w / 2 - 6} ${y + 28} L${x + w / 2} ${y + 35} L${x + w / 2 + 6} ${y + 28} Z`} />
      <text x={x + w / 2} y={y + 19} textAnchor="middle">{children}</text>
    </g>
  );
}

const LOTS: Array<[number, number, number, number, Fill]> = [
  [12, 12, 104, 84, 'hab'], [124, 12, 104, 84, 'verde'], [236, 12, 112, 84, 'hab'], [356, 12, 112, 84, 'equip'],
  [12, 104, 104, 84, 'equip'], [124, 104, 104, 84, 'hab'], [236, 104, 112, 84, 'plain'], [356, 104, 112, 84, 'hab'],
  [12, 196, 104, 92, 'hab'], [124, 196, 104, 92, 'plain'], [236, 196, 112, 92, 'verde'], [356, 196, 112, 92, 'hab'],
];

const DRAWINGS: Record<GuideIllustrationId, { label: string; legend?: Array<[string, string]>; art: ReactNode }> = {
  costStack: {
    label: 'O preço do anúncio é só a base do custo: em cima somam-se impostos, escritura e registos, custos do banco e eventuais obras.',
    art: (
      <>
        <Block x={72} y={150} w={240} h={138} fill="hab" />
        <Label x={192} y={225} anchor="middle" onFill strong>Preço do anúncio</Label>
        <Block x={72} y={100} w={240} h={50} fill="equip" />
        <Label x={192} y={131} anchor="middle" onFill>IMT e Imposto do Selo</Label>
        <Block x={72} y={64} w={240} h={36} fill="tint" />
        <Label x={192} y={87} anchor="middle" onFill>Escritura e registos</Label>
        <Block x={72} y={36} w={240} h={28} fill="verde" />
        <Label x={192} y={55} anchor="middle" onFill>Custos do banco</Label>
        <Block x={72} y={12} w={240} h={24} fill="hatch" />
        <Label x={192} y={29} anchor="middle">Obras</Label>
        <path className="gi-line" d="M328 12 H344 V288 H328" />
        <Label x={356} y={146} strong>Custo</Label>
        <Label x={356} y={166} strong>total</Label>
      </>
    ),
  },
  commute: {
    label: 'Uma casa e o local de trabalho na planta da cidade, ligados por um trajeto de até 30 minutos.',
    legend: [['hab', 'Habitação'], ['equip', 'Equipamentos'], ['verde', 'Espaços verdes'], ['area', 'O teu trajeto']],
    art: (
      <>
        {LOTS.map(([x, y, w, h, fill]) => <Block key={`${x}-${y}`} x={x} y={y} w={w} h={h} fill={fill} />)}
        <path className="gi-route" d="M64 242 H120 V100 H352 V58 H412" />
        <Tag x={196} y={86} w={72}>≤ 30 min</Tag>
        <Pin x={28} y={200}>Casa</Pin>
        <Pin x={368} y={18} w={88}>Trabalho</Pin>
      </>
    ),
  },
  floorPlan: {
    label: 'Planta de um T2 com sala, cozinha, dois quartos e casa de banho.',
    art: (
      <>
        <Block x={40} y={16} w={240} h={160} fill="hab" />
        <Label x={160} y={102} anchor="middle" onFill strong>Sala</Label>
        <Block x={280} y={16} w={160} h={110} fill="tint" />
        <Label x={360} y={76} anchor="middle" onFill>Cozinha</Label>
        <Block x={280} y={126} w={160} h={50} fill="plain" />
        <Label x={360} y={156} anchor="middle">Casa de banho</Label>
        <Block x={40} y={176} w={200} h={88} fill="verde" />
        <Label x={140} y={227} anchor="middle" onFill>Quarto</Label>
        <Block x={240} y={176} w={200} h={88} fill="verde" />
        <Label x={340} y={227} anchor="middle" onFill>Quarto</Label>
        <path className="gi-line" d="M40 274 H440 M40 270 V278 M440 270 V278" />
        <Label x={240} y={296} anchor="middle" mono>área útil, não só a tipologia</Label>
      </>
    ),
  },
  energy: {
    label: 'A escala do certificado energético, de A+, a melhor classe, até F, a mais baixa.',
    art: (
      <>
        {([['A+', 'verde'], ['A', 'verde'], ['B', 'verde'], ['B-', 'hab'], ['C', 'hab'], ['D', 'tint'], ['E', 'equip'], ['F', 'equip']] as Array<[string, Fill]>).map(([grade, fill], index) => (
          <g key={grade}>
            <Block x={24} y={12 + index * 34} w={96 + index * 32} h={28} fill={fill} />
            <Label x={38} y={32 + index * 34} onFill mono strong>{grade}</Label>
          </g>
        ))}
        <Label x={456} y={32} anchor="end" strong>Melhor</Label>
        <Label x={456} y={270} anchor="end" strong>Mais baixa</Label>
      </>
    ),
  },
  lightAndNoise: {
    label: 'Um prédio ao longo do dia: o sol muda de lado, e a rua faz mais ou menos ruído conforme a hora.',
    art: (
      <>
        <path className="gi-route" d="M60 110 V44 H420 V110" />
        <Block x={48} y={96} w={24} h={24} fill="hab" />
        <Label x={60} y={140} anchor="middle">Manhã</Label>
        <Block x={228} y={32} w={24} h={24} fill="hab" />
        <Label x={240} y={24} anchor="middle">Tarde</Label>
        <Block x={408} y={96} w={24} h={24} fill="tint" />
        <Label x={420} y={140} anchor="middle">Noite</Label>
        <Block x={160} y={84} w={160} h={172} fill="plain" />
        {[0, 1, 2].flatMap((row) => [0, 1, 2].map((col) => (
          <Block key={`${row}-${col}`} x={176 + col * 46} y={100 + row * 50} w={32} h={34} fill={row === 1 && col === 1 ? 'hab' : 'paper'} />
        )))}
        <Block x={24} y={256} w={432} h={32} fill="hatch" />
        <Label x={240} y={278} anchor="middle" strong>Rua: ouve-a de dia e à noite</Label>
      </>
    ),
  },
  documents: {
    label: 'Os seis documentos a pedir, empilhados como separadores de um processo.',
    art: (
      <>
        {([['Caderneta predial', 'hab'], ['Certidão do registo predial', 'verde'], ['Licença de utilização', 'tint'], ['Ficha técnica', 'plain'], ['Certificado energético', 'equip'], ['Não dívida ao condomínio', 'hab']] as Array<[string, Fill]>).map(([name, fill], index) => (
          <g key={name}>
            <Block x={16 + index * 14} y={12 + index * 46} w={400} h={44} fill={fill} />
            <Label x={32 + index * 14} y={40 + index * 46} onFill={fill !== 'plain'}>{name}</Label>
          </g>
        ))}
      </>
    ),
  },
  tradeOff: {
    label: 'Duas casas na mesma planta: uma com mais espaço, longe do trajeto; outra mais pequena, numa zona melhor, dentro do trajeto.',
    art: (
      <>
        <Block x={16} y={16} w={208} h={232} fill="plain" />
        <Block x={40} y={40} w={160} h={184} fill="hab" />
        <Label x={120} y={138} anchor="middle" onFill strong>Mais espaço</Label>
        <Label x={120} y={270} anchor="middle" strong>Casa A</Label>
        <Label x={240} y={138} anchor="middle" mono strong>ou</Label>
        <Block x={256} y={16} w={208} h={232} fill="plain" />
        <Block x={272} y={32} w={176} h={88} fill="verde" />
        <Block x={312} y={136} w={96} h={72} fill="hab" />
        <rect className="gi-area" x={280} y={124} width={160} height={108} />
        <Label x={360} y={80} anchor="middle" onFill strong>Melhor zona</Label>
        <Label x={360} y={270} anchor="middle" strong>Casa B</Label>
      </>
    ),
  },
  match: {
    label: 'Exemplo ilustrativo de um resultado do Matchmaker: 78% de Match, com o que encaixa e o que falta confirmar.',
    art: (
      <>
        <Block x={16} y={16} w={448} h={268} fill="paper" />
        <Label x={36} y={46} mono>Exemplo ilustrativo</Label>
        <Block x={36} y={62} w={120} h={72} fill="hab" />
        <Label x={96} y={110} anchor="middle" onFill mono strong>78%</Label>
        <Label x={172} y={92} strong>T2 com varanda</Label>
        <Label x={172} y={116}>Match com o que procuras</Label>
        <path className="gi-line" d="M36 154 H444" />
        <Block x={36} y={170} w={16} h={16} fill="verde" />
        <Label x={64} y={184}>Encaixa: a tipologia e a varanda</Label>
        <Block x={36} y={204} w={16} h={16} fill="tint" />
        <Label x={64} y={218}>A confirmar: o tempo até ao trabalho</Label>
        <Block x={36} y={238} w={16} h={16} fill="hab" />
        <Label x={64} y={252}>Estimativa do que vais pagar</Label>
      </>
    ),
  },
  imtJovem: {
    label: 'O IMT Jovem por valor da casa: 0 euros até 330.539, 8% só sobre o excedente até 660.982, e sem redução acima disso.',
    art: (
      <>
        <Block x={16} y={60} w={196} h={120} fill="verde" />
        <Label x={114} y={114} anchor="middle" onFill mono strong>0 €</Label>
        <Label x={114} y={138} anchor="middle" onFill>de IMT</Label>
        <Block x={212} y={60} w={152} h={120} fill="hab" />
        <Label x={288} y={114} anchor="middle" onFill mono strong>8%</Label>
        <Label x={288} y={138} anchor="middle" onFill>sobre o excedente</Label>
        <Block x={364} y={60} w={100} h={120} fill="hatch" />
        <Label x={414} y={126} anchor="middle" strong>Sem IMT Jovem</Label>
        <path className="gi-line" d="M16 180 V204 M212 180 V204 M364 180 V204" />
        <Label x={16} y={224} mono>0 €</Label>
        <Label x={212} y={224} anchor="middle" mono>330.539 €</Label>
        <Label x={364} y={224} anchor="middle" mono>660.982 €</Label>
        <Label x={240} y={36} anchor="middle" strong>Valor da casa, primeira habitação, Continente</Label>
      </>
    ),
  },
  stampDuty: {
    label: 'As duas taxas do Imposto do Selo: 0,8% na compra e 0,6% no crédito.',
    art: (
      <>
        <rect className="gi-stamp" x={32} y={48} width={196} height={196} />
        <Block x={48} y={64} w={164} h={164} fill="hab" />
        <Label x={130} y={146} anchor="middle" onFill mono strong>0,8%</Label>
        <Label x={130} y={172} anchor="middle" onFill>na compra</Label>
        <rect className="gi-stamp" x={252} y={48} width={196} height={196} />
        <Block x={268} y={64} w={164} h={164} fill="tint" />
        <Label x={350} y={146} anchor="middle" onFill mono strong>0,6%</Label>
        <Label x={350} y={172} anchor="middle" onFill>no crédito</Label>
      </>
    ),
  },
  deedCosts: {
    label: 'Preços fixos do balcão Casa Pronta: 375 euros sem crédito e 700 euros com crédito.',
    art: (
      <>
        <Block x={32} y={136} w={180} h={120} fill="hab" />
        <Label x={122} y={190} anchor="middle" onFill mono strong>375 €</Label>
        <Label x={122} y={216} anchor="middle" onFill>sem crédito</Label>
        <Block x={252} y={32} w={180} h={224} fill="tint" />
        <Label x={342} y={138} anchor="middle" onFill mono strong>700 €</Label>
        <Label x={342} y={164} anchor="middle" onFill>com crédito</Label>
        <path className="gi-line" d="M16 256 H464" />
        <Label x={240} y={282} anchor="middle" strong>Balcão Casa Pronta</Label>
      </>
    ),
  },
  bankCosts: {
    label: 'Intervalos dos custos do banco: avaliação de 200 a 350 euros, dossier de 200 a 300 euros, formalização de 200 a 725 euros.',
    art: (
      <>
        {([['Avaliação', 350, '200 € a 350 €'], ['Dossier', 300, '200 € a 300 €'], ['Formalização', 725, '200 € a 725 €']] as Array<[string, number, string]>).map(([name, top, range], index) => (
          <g key={name}>
            <Label x={24} y={52 + index * 88} strong>{name}</Label>
            <Label x={456} y={52 + index * 88} anchor="end" mono>{range}</Label>
            <Block x={24} y={62 + index * 88} w={432} h={30} fill="plain" />
            <Block x={24 + (200 / 725) * 432} y={62 + index * 88} w={((top - 200) / 725) * 432} h={30} fill="hab" />
          </g>
        ))}
      </>
    ),
  },
  building: {
    label: 'Um prédio em alçado, com telhado, janelas, elevador e garagem, as partes comuns a ver na visita.',
    art: (
      <>
        <Block x={96} y={16} w={288} h={36} fill="hatch" />
        <Label x={240} y={40} anchor="middle" strong>Telhado</Label>
        <Block x={112} y={52} w={256} h={188} fill="plain" />
        {[0, 1, 2].flatMap((row) => [0, 1].map((col) => (
          <Block key={`${row}-${col}`} x={128 + col * 72} y={68 + row * 56} w={56} h={40} fill={row === 0 ? 'hab' : 'paper'} />
        )))}
        <Block x={284} y={68} w={68} h={152} fill="tint" />
        <Label x={318} y={148} anchor="middle" onFill>Elevador</Label>
        <Block x={96} y={240} w={288} h={44} fill="verde" />
        <Label x={240} y={268} anchor="middle" onFill strong>Garagem</Label>
      </>
    ),
  },
  checklistSheet: {
    label: 'Uma folha de verificação com vários pontos já assinalados na visita.',
    art: (
      <>
        <Block x={72} y={12} w={336} h={276} fill="paper" />
        <Block x={72} y={12} w={336} h={44} fill="hab" />
        <Label x={240} y={40} anchor="middle" onFill strong>Visita à casa</Label>
        {['Humidade e mofo', 'Pressão da água', 'Ruído da rua', 'Luz em cada divisão', 'Quotas do condomínio'].map((item, index) => (
          <g key={item}>
            <rect className="gi-block gi-paper" x={92} y={74 + index * 42} width={22} height={22} />
            {index < 3 && <path className="gi-check" d={`M96 ${85 + index * 42} l5 6 l9 -12`} />}
            <Label x={128} y={91 + index * 42}>{item}</Label>
            {index < 3 && <path className="gi-line" d={`M128 ${86 + index * 42} H${128 + item.length * 7.4}`} />}
          </g>
        ))}
      </>
    ),
  },
};

export function GuideIllustration({ id }: { id: GuideIllustrationId }) {
  const { label, legend, art } = DRAWINGS[id];
  return (
    <figure className="gi" role="img" aria-label={label}>
      <svg className="gi-svg" viewBox="0 0 480 300" aria-hidden="true">
        <defs>
          <pattern id={`gi-hatch-${id}`} width="8" height="8" patternUnits="userSpaceOnUse">
            <path className="gi-hatch-line" d="M-1 1l2-2M0 8l8-8M7 9l2-2" />
          </pattern>
        </defs>
        <g style={{ '--gi-hatch-url': `url(#gi-hatch-${id})` } as React.CSSProperties}>{art}</g>
      </svg>
      {legend && (
        <ul className="zp-legend" aria-hidden="true">
          {legend.map(([key, name]) => <li key={key}><span className={`zp-swatch zp-swatch-${key}`} />{name}</li>)}
        </ul>
      )}
    </figure>
  );
}
